'use client';

import { LlamaErrorScreen } from '@/components/ErrorScreens/LlamaErrorScreen';
import { useEffect } from 'react';
import { Work_Sans } from 'next/font/google';
import '@/app/globals.css';
import '@liveauctioneers/hammer-ui-theme/css/Wavebid.css';

// Registers Work Sans @font-face for --font-family-primary so Hammer Button text doesn't
// fall back to serif — Wavebid.css only names the font, next/font actually loads it.
const workSansPrimary = Work_Sans({
    subsets: ['latin'],
    variable: '--font-family-primary',
    weight: ['400', '500', '600', '700'],
});

export type GlobalErrorContentProps = {
    error: Error & { digest?: string };
    reset: () => void;
};

/**
 * Content component for the global error boundary.
 *
 * This component contains the actual error handling logic and UI,
 * separated from the HTML document structure required by Next.js global-error.tsx.
 *
 * Handles:
 * - Development logging via useEffect
 * - LlamaErrorScreen rendering with appropriate callbacks
 * - Environment-aware error details visibility
 *
 * @see https://nextjs.org/docs/app/api-reference/file-conventions/error#global-errorjs
 */
export const GlobalErrorContent = ({ error, reset }: GlobalErrorContentProps) => {
    useEffect(() => {
        // Log error in development
        if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            console.error('Global error:', error);
        }
    }, [error]);

    return (
        <div className="hui-wavebid">
            <div className={workSansPrimary.variable}>
                <LlamaErrorScreen
                    error={error}
                    onGoHome={() => (window.location.href = '/')}
                    onReset={() => reset()}
                    showDetails={process.env.NODE_ENV === 'development'}
                />
            </div>
        </div>
    );
};

GlobalErrorContent.displayName = 'GlobalErrorContent';
